import React from "react";
import { rp, fmtDate, fmtTime, BOOKING_STATUSES, PAYMENT_STATUSES, DEPOSIT_STATUSES } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

export const sel = "w-full h-10 rounded-md border border-input bg-white px-3 text-sm";

export const L = ({ label, children }) => (
  <label className="block"><span className="text-[11px] uppercase tracking-widest text-ink-soft">{label}</span><div className="mt-1">{children}</div></label>
);

const R = ({ l, v, bold, testid }) => (
  <div className="flex justify-between gap-3"><span className="text-ink-soft">{l}</span>
    <span data-testid={testid} className={bold ? "font-semibold" : ""}>{v}</span></div>
);

const vehicleLabel = (v) => (v === "bike" ? "Bike" : v === "car" ? "Car / MPV" : "Self Pickup");

export const BookingTotals = ({ b }) => (
  <section className="bg-white border border-black/10 rounded-lg p-5 text-sm space-y-1.5">
    <h2 className="text-base font-semibold mb-2">Totals</h2>
    <R l="Package" v={`${b.package_name_snapshot} (${b.package_duration_snapshot})`} />
    {b.auto_upgraded_from && <R l="Auto-upgraded from" v={`${b.auto_upgraded_from} (ended after closing)`} />}
    <R l="End Time" v={`${fmtTime(b.end_datetime)} · ${fmtDate(b.end_datetime?.slice(0, 10))}`} />
    <R l="Rental Subtotal" v={rp(b.rental_subtotal)} />
    <R l="Add-ons" v={rp(b.addon_total)} />
    <R l="Delivery + Pickup" v={`${rp(b.delivery_fee + b.pickup_fee)} · ${vehicleLabel(b.vehicle_type)}`} />
    <R l="Sumber" v={b.source || "—"} />    <R l="Promo" v={`-${rp(b.promo_discount)}`} />
    <R l="Manual Discount" v={`-${rp(b.manual_discount)}`} />
    {b.pickup_discount > 0 && <R l={b.pickup_discount_label || "Self Pickup Discount"} v={`-${rp(b.pickup_discount)}`} />}
    {b.loyalty_discount > 0 && <R l={`${b.loyalty_tier} Member -${b.loyalty_discount_percent}%`} v={`-${rp(b.loyalty_discount)}`} />}
    <R l="Rental Total" v={rp(b.final_rental_total)} bold />
    <R l="Deposit" v={rp(b.deposit_amount)} />
    <R l="TOTAL PAYMENT" v={rp(b.total_payment)} bold testid="bd-total" />
    <R l="Amount Paid" v={rp(b.amount_paid)} />
    <R l="Remaining" v={rp(Math.max(0, b.total_payment - b.amount_paid))} />
  </section>
);

export const BookingStatusBar = ({ b, save }) => (
  <section className="bg-white border border-black/10 rounded-lg p-5 grid sm:grid-cols-3 gap-3">
    <L label="Sumber Booking"><select data-testid="bd-source" className={sel} value={b.source || ""}
      onChange={(e) => save({ source: e.target.value })}>
      <option value="">Tidak diketahui</option>
      {["Instagram", "WhatsApp", "Google", "TikTok", "Teman / Referral", "Website", "Admin", "Lainnya"].map((s) => <option key={s}>{s}</option>)}
    </select></L>
    <L label="Booking Status"><select data-testid="bd-booking-status" className={sel} value={b.booking_status}
      onChange={(e) => save({ booking_status: e.target.value })}>{BOOKING_STATUSES.map((s) => <option key={s}>{s}</option>)}</select></L>
    <L label="Payment Status"><select data-testid="bd-payment-status" className={sel} value={b.payment_status}
      onChange={(e) => save({ payment_status: e.target.value })}>{PAYMENT_STATUSES.map((s) => <option key={s}>{s}</option>)}</select></L>
    <L label="Deposit Status"><select data-testid="bd-deposit-status" className={sel} value={b.deposit_status}
      onChange={(e) => save({ deposit_status: e.target.value })}>{DEPOSIT_STATUSES.map((s) => <option key={s}>{s}</option>)}</select></L>
  </section>
);

export const AssignUnitsCard = ({ b, units, assign }) => (
  <section className="bg-white border border-black/10 rounded-lg p-5">
    <h2 className="text-base font-semibold">Assign Units</h2>
    <div className="mt-3 flex flex-wrap gap-2">
      {units.map((u) => {
        const on = (b.assigned_units || []).includes(u.id);
        return <button key={u.id} data-testid={`assign-${u.unit_code}`} onClick={() => assign(u.id)}
          className={`text-xs px-3 py-1.5 rounded-full border ${on ? "bg-lm-red text-white border-lm-red" : "border-black/15"}`}>{u.unit_code}</button>;
      })}
    </div>
  </section>
);

export const AssignDriverCard = ({ b, drivers, assignDriver }) => (
  <section className="bg-white border border-black/10 rounded-lg p-5" data-testid="bd-assign-driver">
    <h2 className="text-base font-semibold">Assign Driver</h2>
    <p className="text-xs text-ink-soft mt-1">The driver only sees the stops assigned to them in Driver Capture.</p>
    <div className="mt-3">
      <select data-testid="bd-driver-select" className={sel} value={b.assigned_driver_id || ""}
        onChange={(e) => assignDriver(e.target.value)}>
        <option value="">— Not assigned —</option>
        {drivers.map((d) => <option key={d.user_id} value={d.user_id}>{d.name}</option>)}
      </select>
    </div>
    {drivers.length === 0 && <p className="mt-2 text-xs text-amber-700">No driver accounts yet — create one in Staff Accounts.</p>}
  </section>
);

export const DepositRefundCard = ({ b, refund, setRefund, submitRefund, voidRefund, refundWaHref, damage }) => (
  <section className="bg-white border border-black/10 rounded-lg p-5" data-testid="bd-deposit-refund">
    <h2 className="text-base font-semibold">Deposit Refund</h2>
    <div className="mt-3 grid sm:grid-cols-2 gap-3">
      <L label="Deduction Amount"><Input data-testid="dr-deduction" type="number" value={refund.deduction_amount} onChange={(e) => setRefund({ ...refund, deduction_amount: e.target.value })} /></L>
      <L label="Refunded Amount"><Input data-testid="dr-refunded" type="number" value={refund.refunded_amount} onChange={(e) => setRefund({ ...refund, refunded_amount: e.target.value })} /></L>
      <L label="Method"><select className={sel} value={refund.method} onChange={(e) => setRefund({ ...refund, method: e.target.value })}>
        {["Bank Transfer", "QRIS", "Cash", "Other"].map((m) => <option key={m}>{m}</option>)}</select></L>
      <L label="Refund Date"><Input type="date" value={refund.returned_date} onChange={(e) => setRefund({ ...refund, returned_date: e.target.value })} /></L>
      <div className="sm:col-span-2"><L label="Reason / Notes"><Textarea rows={2} value={refund.reason} onChange={(e) => setRefund({ ...refund, reason: e.target.value })} /></L></div>
    </div>
    <div className="mt-3 flex gap-2 flex-wrap">
      <button data-testid="dr-submit" onClick={submitRefund} className="lm-btn-primary rounded-md px-5 py-2.5 text-sm font-semibold">Record Deposit Refund</button>
      {b.deposit_receipt && (
        <a data-testid="dr-wa" target="_blank" rel="noreferrer" href={refundWaHref}
          className="rounded-md px-5 py-2.5 text-sm border border-black/15">Send Receipt on WhatsApp</a>
      )}
    </div>
    {b.deposit_receipt && (
      <div className="mt-3 text-sm text-ink-soft" data-testid="dr-receipt">
        Receipt {b.deposit_receipt.receipt_no} · refunded {rp(b.deposit_receipt.refunded_amount)} on {b.deposit_receipt.returned_date} ({b.deposit_receipt.method})
      </div>
    )}
    {(b.deposit_refund_history || []).length > 0 && (
      <div className="mt-3 border-t border-black/5 pt-3" data-testid="dr-history">
        <div className="text-[11px] uppercase tracking-widest text-ink-soft">Refund History</div>
        <div className="mt-2 text-sm divide-y divide-black/5">
          {b.deposit_refund_history.map((r) => (
            <div key={r.receipt_no + r.created_at} className="flex justify-between items-center gap-3 py-2" data-testid={`dr-history-${r.receipt_no}`}>
              <span className="text-ink-soft">{r.returned_date} · {r.receipt_no} · {r.method}{r.reason ? ` · ${r.reason}` : ""}</span>
              <span className="shrink-0 flex items-center gap-2">
                {rp(r.refunded_amount)}{r.deduction_amount > 0 ? ` (−${rp(r.deduction_amount)})` : ""}
                <button data-testid={`dr-void-${r.receipt_no}`} title="Undo this refund"
                  onClick={() => voidRefund(r.receipt_no)}
                  className="text-xs px-2 py-1 rounded border border-black/15 text-lm-red hover:border-lm-red">Undo</button>
              </span>
            </div>
          ))}
        </div>
        <div className="mt-2 flex justify-between text-sm font-semibold">
          <span>Total refunded</span>
          <span data-testid="dr-history-total">{rp(b.deposit_refunded_total || 0)}</span>
        </div>
      </div>
    )}
    {damage && (
      <div className="mt-4 border-t border-black/5 pt-3">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="text-sm">
            <span className="font-semibold">Damage Evidence Pack</span>
            <div className="text-xs text-ink-soft mt-0.5">
              {damage.issues.length} issue(s) · repair {rp(damage.repair_cost)} · deduction {rp(damage.deduction)} · {damage.media_count} media
            </div>
          </div>
          <a data-testid="damage-pack-btn" href={damage.whatsapp_url} target="_blank" rel="noreferrer"
            className="rounded-md px-4 py-2.5 text-sm border border-black/15 hover:border-lm-red">Send Damage Pack on WhatsApp</a>
        </div>
        <pre className="mt-3 whitespace-pre-wrap font-sans text-xs text-ink-soft" data-testid="damage-pack-msg">{damage.message}</pre>
      </div>
    )}
  </section>
);

export const PaymentsCard = ({ b, pay, setPay, addPayment, syncMidtrans, syncing }) => (
  <section className="bg-white border border-black/10 rounded-lg p-5">
    <div className="flex items-center justify-between gap-3 flex-wrap">
      <h2 className="text-base font-semibold">Payments</h2>
      <button data-testid="bd-sync-midtrans" onClick={syncMidtrans} disabled={syncing}
        className="h-9 px-3 rounded-md border border-black/15 text-xs hover:border-lm-red disabled:opacity-50">
        {syncing ? "Mengecek…" : "Cek status di Midtrans"}
      </button>
    </div>
    <div className="mt-3 grid sm:grid-cols-4 gap-2">
      <Input data-testid="bd-pay-amount" type="number" placeholder="Amount" value={pay.amount} onChange={(e) => setPay({ ...pay, amount: e.target.value })} />
      <select className={sel} value={pay.payment_method} onChange={(e) => setPay({ ...pay, payment_method: e.target.value })}>
        {["Bank Transfer", "QRIS", "Cash", "Other"].map((m) => <option key={m}>{m}</option>)}</select>
      <select className={sel} value={pay.payment_type} onChange={(e) => setPay({ ...pay, payment_type: e.target.value })}>
        {["Rental", "DP", "Deposit", "Refund"].map((m) => <option key={m}>{m}</option>)}</select>
      <button data-testid="bd-add-payment" onClick={addPayment} className="lm-btn-primary rounded-md text-sm font-semibold">Add</button>
    </div>
    <div className="mt-3 text-sm divide-y divide-black/5">
      {(b.payments || []).map((p) => (
        <div key={p.id} className="flex justify-between py-2"><span>{p.payment_date} · {p.payment_method} · {p.payment_type}</span><span>{rp(p.amount)}</span></div>
      ))}
      {(b.payments || []).length === 0 && <div className="py-2 text-ink-soft">No payments yet.</div>}
    </div>
  </section>
);
